import { buildPrompt } from '@/ai/utils/promptBuilder'
import { constructMessagesHistory } from '@/ai/prompts/common/messagesHistory'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'
import { functionType } from '@/api/chatGPT/chatLog/functions'
import { workflowDataExample } from '@/ai/prompts/common/examples'
import markdownContent from '@/ai/prompts/markdown/systemPromptWorkflowAssistant.md'

export const buildWorkflowAssistantPrompt = ({ workflow, messagesHistory, prompt, currentUIState }) => {
  const messages = []

  const workflowData = {
    id: workflow.id,
    name: workflow.name
  }

  const events = (workflow.eventsJson ?? []).map((event) => {
    const eventDataModels = (event.dataModels || [])?.map((dataModel) => {
      return {
        id: dataModel.id,
        name: dataModel.name,
        type: dataModel.type,
        cardId: dataModel.cardId,
        dataFields: dataModel.dataFields?.map((item) => ({
          name: item.name,
          cardinality: item.cardinality,
          exampleData: item.exampleData,
          relatedEntityId: item.relatedEntityId
        }))
      }
    })

    const parentEventNames = event.parents?.map((item) => {
      if (item === 'start') {
        return 'start'
      }

      return workflow.eventsJson.find((e) => e.id === item)?.description || null
    })

    const cards = event.requirementsJson?.map((card) => ({
      id: card.id,
      description: card.description,
      cardType: { id: card.cardType.id, title: card.cardType.title }
    }))

    return {
      id: event.id,
      description: event.description,
      laneId: event.laneId,
      parentEventNames,
      cards,
      dataModels: eventDataModels
    }
  })

  const entities = (workflow.dataModels ?? []).map((entity) => {
    return {
      id: entity.id,
      name: entity.name,
      dataFields: (entity.dataFields ?? []).map((item) => ({
        name: item.name,
        cardinality: item.cardinality,
        exampleData: item.exampleData,
        primaryKey: item.primaryKey,
        reference: item.reference,
        relatedEntityId: item.relatedEntityId,
        isRequired: !!item.tags?.includes('REQUIRED')
      }))
    }
  })

  const promptSections = []

  promptSections.push({
    content: markdownContent,
    flattenLines: false
  })

  promptSections.push({
    title: 'Available Actions',
    contentType: 'bullet',
    content: Object.values(functionType)
  })

  promptSections.push({
    title: 'NOT Available Actions',
    contentType: 'bullet',
    content: [
      'Move (drag/drop) Events',
      'Move (drag/drop) Cards',
      'Create/update/delete groups',
      'Create/update/delete read models',
      'Update/delete write models',
      'Rollback change',
      'Create connection between existing events'
    ]
  })

  promptSections.push({
    title: 'Important Notes',
    content: [
      'If the user\'s request corresponds to one of the above actions, respond with a function call.',
      `If not, instead reply in plain text with the message explaining that the action is not
       available yet. For example if the user asks to move an event, do not attempt to call the
       "moveLane" action just because it contains the word "move".`,
      `If any essential detail is missing or ambiguous (for instance, the user says "rename the event" but doesn't
       specify which event), pause and ask a follow-up question. Do not call any function until you have enough
       information to proceed safely.`
    ]
  })

  promptSections.push({
    title: 'Workflow UI Overview',
    contentType: 'bullet',
    content: [
      `**Workflow Diagram**: At the top of the screen, events appear as "sticky-note" boxes arranged left-to-right in
       (typically) chronological order. Arrows connect each event to its parent(s).`,
      `**Event Cards Sidebar & Backlog Tab**: Each event has associated cards of various types. These cards are visible
       in a sidebar. There is also a "Backlog" tab that shows only the cards flagged for backlog review.`,
      `**Entities Tab**: Under the "Entities" tab, you see all global data models (entities) used in this workflow.
       Each entity lists its dataFields (name, cardinality, exampleData, primaryKey, etc.).`,
      `**Domain Model Tab**: Under "Domain Model," read models and write models (commands) appear connected by
       arrows to the corresponding entities. Commands (write models) show the role (lane) that issues them,
       the aggregate root entity they modify, and the resulting event. Entities are grouped by aggregate
       root - multiple commands can attach to the same root entity if they operate on it.`
    ]
  })

  promptSections.push({
    title: 'Workflow Data',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify(workflowData)
  })

  promptSections.push({
    title: 'Card Types',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify(workflow.cardTypes || [])
  })

  if (!events.length) {
    promptSections.push({
      title: 'Example Event Structure',
      contentType: 'codeblock',
      language: 'json',
      content: JSON.stringify(workflowDataExample.events)
    })
  } else {
    promptSections.push({
      title: 'Workflow Events',
      contentType: 'codeblock',
      language: 'json',
      content: JSON.stringify(events)
    })
  }

  if (!workflow.lanes?.length) {
    promptSections.push({
      title: 'Example Lanes Structure',
      contentType: 'codeblock',
      language: 'json',
      content: JSON.stringify(workflowDataExample.lanes)
    })
  } else {
    promptSections.push({
      title: 'Workflow Lanes (Roles)',
      contentType: 'codeblock',
      language: 'json',
      content: JSON.stringify(workflow.lanes || [])
    })
  }

  if (!entities.length) {
    promptSections.push({
      title: 'Example Entities Structure',
      contentType: 'codeblock',
      language: 'json',
      content: JSON.stringify(workflowDataExample.entities)
    })
  } else {
    promptSections.push({
      title: 'Workflow Entities',
      contentType: 'codeblock',
      language: 'json',
      content: JSON.stringify(entities || [])
    })
  }

  const systemContent = buildPrompt(promptSections)
  messages.push(createSystemMessage(systemContent))

  const outputs = constructMessagesHistory(messagesHistory)

  outputs.forEach((output) => {
    messages.push(output)
  })

  let prefix = ''
  if (currentUIState?.selectedTabNum && typeof currentUIState?.selectedTabNum !== 'undefined' && currentUIState?.selectedTabName) {
    prefix += `[Selected Tab: ${currentUIState.selectedTabNum} - ${currentUIState.selectedTabName}] `
  }
  if (currentUIState?.selectedEventId && currentUIState?.selectedEventName) {
    prefix += `[Selected Event: "${currentUIState.selectedEventId} - ${currentUIState.selectedEventName}"] `
  }
  if (prefix !== '') {
    prefix += '\n'
  }

  messages.push(createUserMessage(prefix + prompt, currentUIState))

  return messages
}
